<template>
  <div v-if="show" class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
    <div class="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl animate-in fade-in zoom-in-95 duration-200">

      <!-- Modal Header -->
      <div class="px-6 py-4 border-b border-zinc-800 flex justify-between items-center shrink-0">
        <div>
          <h2 class="text-lg font-bold text-zinc-100">{{ poi ? 'Modifier le point d\'intérêt' : 'Ajouter un point d\'intérêt' }}</h2>
          <p v-if="step === 'search'" class="text-xs text-zinc-500 mt-0.5">Recherchez un lieu pour le pré-remplir automatiquement</p>
          <p v-else class="text-xs text-zinc-500 mt-0.5">Complétez les informations du lieu</p>
        </div>
        <button @click="$emit('close')" class="text-zinc-400 hover:text-zinc-100 transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>
      </div>

      <!-- Step 1: Search autocomplete -->
      <div v-if="step === 'search'" class="p-6 space-y-4 flex-1">

        <!-- Search input -->
        <div class="space-y-1 relative">
          <label class="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Rechercher un lieu</label>
          <div class="relative">
            <input
              ref="searchInputRef"
              type="text"
              v-model="searchAddressQuery"
              @input="handleAddressSearchInput"
              placeholder="Monument, adresse, ville, restaurant..."
              data-dashlane-ignore="true" data-1p-ignore="true" data-lpignore="true" data-form-type="other" autocomplete="off"
              class="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 pr-10 text-sm text-zinc-100 placeholder-zinc-700 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none transition-all"
            />
            <span v-if="suggestionsLoading" class="absolute right-3 top-3.5 w-4 h-4 border-2 border-sky-500 border-t-transparent rounded-full animate-spin"></span>
            <svg v-else class="absolute right-3 top-3.5 w-4 h-4 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0"></path></svg>
          </div>
          <!-- Dropdown suggestions -->
          <div v-if="suggestions.length > 0" class="absolute left-0 right-0 mt-1 bg-zinc-950 border border-zinc-800 rounded-xl max-h-72 overflow-y-auto z-50 shadow-2xl divide-y divide-zinc-900/50">
            <button v-for="sug in suggestions" :key="sug.place_id" type="button" @click="selectSuggestion(sug)"
                    class="w-full text-left p-3 hover:bg-zinc-800/80 transition-colors text-xs text-zinc-200 flex flex-col gap-0.5">
              <span class="font-bold text-zinc-100 truncate">{{ sug.display_name }}</span>
              <span class="text-[10px] text-zinc-500">{{ parseFloat(sug.lat).toFixed(4) }}, {{ parseFloat(sug.lon).toFixed(4) }}</span>
            </button>
          </div>
        </div>

        <!-- AI Suggesting feedback -->
        <div v-if="aiSuggesting" class="flex items-center gap-3 p-4 bg-sky-500/5 border border-sky-500/20 rounded-xl">
          <span class="w-5 h-5 border-2 border-sky-500 border-t-transparent rounded-full animate-spin shrink-0"></span>
          <span class="text-sm text-sky-400">L'IA analyse le lieu et recherche une photo...</span>
        </div>

        <!-- Skip button -->
        <div class="pt-2 text-center">
          <button @click="skipToForm" type="button" class="text-zinc-500 hover:text-zinc-300 text-sm transition-colors underline underline-offset-4">
            Créer sans autocomplétion
          </button>
        </div>
      </div>

      <!-- Step 2: Full form -->
      <div v-else class="p-6 overflow-y-auto space-y-6 text-left flex-1">

        <!-- Back to search (only for new POI) -->
        <button v-if="!poi" @click="step = 'search'" type="button"
                class="flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-300 transition-colors -mt-1">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path></svg>
          Retour à la recherche
        </button>

        <!-- Nom & Description -->
        <div class="space-y-4">
          <div class="space-y-1">
            <label class="text-xs font-semibold text-zinc-400 uppercase tracking-wider flex items-center gap-2">
              Nom du lieu *
            </label>
            <input type="text" v-model="form.name" placeholder="Ex: Randonnée Cascade du Gier, Restaurant Le Panoramique..." required
                   data-dashlane-ignore="true" data-1p-ignore="true" data-lpignore="true" data-form-type="other" autocomplete="off"
                   class="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 text-sm text-zinc-100 placeholder-zinc-700 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none transition-all" />
          </div>

          <div class="space-y-1">
            <label class="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Description</label>
            <textarea v-model="form.description" rows="3" placeholder="Informations utiles, avis personnel, horaires, conseils d'accès..."
                      class="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 text-sm text-zinc-100 placeholder-zinc-700 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none transition-all resize-none"></textarea>
          </div>
        </div>

        <!-- Adresse -->
        <div class="space-y-3">
          <div class="space-y-1 relative">
            <label class="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Adresse</label>
            <div class="relative">
              <input type="text" v-model="form.address" placeholder="Adresse complète ou région (ex: Pilat, France)"
                     data-dashlane-ignore="true" data-1p-ignore="true" data-lpignore="true" data-form-type="other" autocomplete="off"
                     class="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 pr-10 text-sm text-zinc-100 placeholder-zinc-700 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none transition-all" />
              <!-- Autocomplete for address in form -->
              <button v-if="!showAddressSearch" @click="showAddressSearch = true" type="button"
                      class="absolute right-3 top-3 text-zinc-500 hover:text-sky-400 transition-colors" title="Rechercher une adresse">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0"></path></svg>
              </button>
            </div>

            <!-- Inline address autocomplete in form -->
            <div v-if="showAddressSearch" class="space-y-1 relative">
              <div class="relative">
                <input
                  type="text"
                  v-model="searchAddressQuery"
                  @input="handleAddressSearchInput"
                  placeholder="Rechercher une adresse..."
                  data-dashlane-ignore="true" data-1p-ignore="true" data-lpignore="true" data-form-type="other" autocomplete="off"
                  class="w-full bg-zinc-950 border border-sky-500/40 rounded-xl p-3 pr-10 text-sm text-zinc-100 placeholder-zinc-700 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none transition-all"
                />
                <span v-if="suggestionsLoading" class="absolute right-3 top-3.5 w-4 h-4 border-2 border-sky-500 border-t-transparent rounded-full animate-spin"></span>
                <button v-else @click="showAddressSearch = false; suggestions = []; searchAddressQuery = ''" type="button"
                        class="absolute right-3 top-3.5 text-zinc-500 hover:text-zinc-300">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
              </div>
              <div v-if="suggestions.length > 0" class="absolute left-0 right-0 mt-1 bg-zinc-950 border border-zinc-800 rounded-xl max-h-60 overflow-y-auto z-50 shadow-2xl divide-y divide-zinc-900/50">
                <button v-for="sug in suggestions" :key="sug.place_id" type="button" @click="selectSuggestionInForm(sug)"
                        class="w-full text-left p-3 hover:bg-zinc-800/80 transition-colors text-xs text-zinc-200 flex flex-col gap-0.5">
                  <span class="font-bold text-zinc-100 truncate">{{ sug.display_name }}</span>
                  <span class="text-[10px] text-zinc-500">{{ parseFloat(sug.lat).toFixed(4) }}, {{ parseFloat(sug.lon).toFixed(4) }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Expandable detailed address -->
          <button @click="showDetailedAddress = !showDetailedAddress" type="button"
                  class="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300 transition-colors">
            <svg class="w-3.5 h-3.5 transition-transform duration-200" :class="showDetailedAddress ? 'rotate-90' : ''"
                 fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
            {{ showDetailedAddress ? 'Masquer' : 'Voir' }} les détails (ville, région, pays, coordonnées)
          </button>

          <div v-if="showDetailedAddress" class="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in slide-in-from-top-2 duration-200">
            <div class="space-y-1">
              <label class="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Ville</label>
              <input type="text" v-model="form.city" placeholder="Ex: Lyon"
                     data-dashlane-ignore="true" data-1p-ignore="true" data-lpignore="true" data-form-type="other" autocomplete="off"
                     class="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 text-sm text-zinc-100 placeholder-zinc-700 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none transition-all" />
            </div>

            <div class="space-y-1">
              <label class="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Région</label>
              <input type="text" v-model="form.region" placeholder="Ex: Auvergne-Rhône-Alpes"
                     data-dashlane-ignore="true" data-1p-ignore="true" data-lpignore="true" data-form-type="other" autocomplete="off"
                     class="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 text-sm text-zinc-100 placeholder-zinc-700 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none transition-all" />
            </div>

            <div class="space-y-1">
              <label class="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Pays</label>
              <input type="text" v-model="form.country" placeholder="Ex: France"
                     data-dashlane-ignore="true" data-1p-ignore="true" data-lpignore="true" data-form-type="other" autocomplete="off"
                     class="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 text-sm text-zinc-100 placeholder-zinc-700 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none transition-all" />
            </div>

            <div class="space-y-1">
              <label class="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Latitude</label>
              <input type="number" step="any" v-model="form.latitude" placeholder="Ex: 45.4167"
                     data-dashlane-ignore="true" data-1p-ignore="true" data-lpignore="true" data-form-type="other" autocomplete="off"
                     class="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 text-sm text-zinc-100 placeholder-zinc-700 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none transition-all" />
            </div>

            <div class="space-y-1">
              <label class="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Longitude</label>
              <input type="number" step="any" v-model="form.longitude" placeholder="Ex: 4.5833"
                     data-dashlane-ignore="true" data-1p-ignore="true" data-lpignore="true" data-form-type="other" autocomplete="off"
                     class="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 text-sm text-zinc-100 placeholder-zinc-700 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none transition-all" />
            </div>

            <div class="md:col-span-2">
              <p class="text-[10px] text-zinc-600">Astuce : vous pouvez aussi cliquer directement sur la carte en arrière-plan pour positionner le marqueur.</p>
            </div>
          </div>
        </div>

        <!-- Tags -->
        <div class="space-y-2">
          <label class="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Tags / Catégories</label>
          <div class="flex gap-2">
            <input type="text" v-model="newTag" @keydown.enter.prevent="addTag" placeholder="Ex: Rando, Cascade, Resto..."
                   class="flex-1 bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-sm text-zinc-100 placeholder-zinc-700 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none transition-all" />
            <button @click="addTag" type="button" class="bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 border border-sky-500/30 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors">
              Ajouter
            </button>
          </div>
          <div v-if="form.tags.length > 0" class="flex flex-wrap gap-2 pt-1">
            <div v-for="tag in form.tags" :key="tag" class="bg-sky-500/10 border border-sky-500/20 text-sky-400 px-3 py-1.5 rounded-lg flex items-center gap-2 text-xs font-medium">
              <span>{{ tag }}</span>
              <button @click="removeTag(tag)" type="button" class="text-sky-500/50 hover:text-sky-400">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
          </div>
          <div v-else class="text-xs text-zinc-600 italic">Aucun tag pour le moment.</div>
        </div>

        <!-- Liens externes -->
        <div class="space-y-3">
          <label class="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Liens d'intérêt</label>
          <div v-for="(link, idx) in form.links" :key="idx" class="flex gap-2 items-center">
            <input type="text" v-model="link.title" placeholder="Titre du lien (ex: AllTrails)" class="w-1/3 bg-zinc-950 border border-zinc-800 rounded-xl p-2.5 text-xs text-zinc-100 placeholder-zinc-700 outline-none focus:border-sky-500" />
            <input type="url" v-model="link.url" placeholder="https://..." class="flex-1 bg-zinc-950 border border-zinc-800 rounded-xl p-2.5 text-xs text-zinc-100 placeholder-zinc-700 outline-none focus:border-sky-500" />
            <button @click="removeLink(idx)" type="button" class="text-zinc-500 hover:text-red-400 p-2"><svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg></button>
          </div>
          <button @click="addLink" type="button" class="text-sky-400 hover:text-sky-300 text-xs font-semibold flex items-center gap-1">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path></svg>
            Ajouter un lien d'intérêt
          </button>
        </div>

        <!-- Médias / Upload -->
        <div class="space-y-3">
          <label class="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Photos & Vidéos (Sécurisés)</label>

          <!-- Existing Media List -->
          <div v-if="poi && form.media.length > 0" class="grid grid-cols-4 gap-3">
            <div v-for="med in form.media" :key="med.id" class="relative group bg-zinc-950 rounded-xl border border-zinc-800 overflow-hidden aspect-video flex items-center justify-center">
              <img v-if="med.type === 'image'" :src="med.url" class="object-cover w-full h-full" />
              <div v-else class="flex flex-col items-center justify-center text-zinc-500">
                <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>
                <span class="text-[9px] mt-1 font-semibold uppercase">Vidéo</span>
              </div>
              <button @click="deleteExistingMedia(med.id)" type="button" class="absolute top-1.5 right-1.5 bg-red-500/80 hover:bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
              </button>
            </div>
          </div>

          <!-- Suggested AI Image Preview -->
          <div v-if="suggestedImageUrl" class="p-4 bg-sky-500/5 border border-sky-500/10 rounded-2xl flex flex-col md:flex-row gap-4 items-center animate-in fade-in duration-300">
            <div class="w-full md:w-1/3 aspect-video bg-zinc-950 rounded-xl overflow-hidden border border-zinc-800 shrink-0">
              <img :src="suggestedImageUrl" class="object-cover w-full h-full" />
            </div>
            <div class="flex-1 text-center md:text-left space-y-2">
              <h4 class="text-xs font-bold text-sky-400">Photo d'illustration suggérée</h4>
              <p class="text-[10px] text-zinc-400">Source Wikipedia — photo libre de droits. Elle sera enregistrée dans votre galerie.</p>
              <label class="flex items-center gap-2 cursor-pointer justify-center md:justify-start">
                <input type="checkbox" v-model="keepSuggestedImage" class="rounded border-zinc-700 bg-zinc-950 text-sky-500 focus:ring-sky-500" />
                <span class="text-xs text-zinc-300">Enregistrer cette photo avec le lieu</span>
              </label>
            </div>
          </div>

          <!-- Drag and drop zone -->
          <div @dragover.prevent @drop.prevent="handleFilesDrop" @click="triggerFileInput"
               class="border-2 border-dashed border-zinc-800 hover:border-sky-500/50 rounded-2xl p-6 text-center cursor-pointer bg-zinc-950/20 hover:bg-zinc-950/40 transition-all flex flex-col items-center justify-center min-h-24">
            <svg class="w-8 h-8 text-zinc-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
            <span class="text-xs text-zinc-400">Glissez-déposez vos fichiers ici, ou cliquez pour parcourir</span>
            <span class="text-[10px] text-zinc-600 mt-1">Formats acceptés : JPG, PNG, MP4. Stockage crypté.</span>
            <input type="file" ref="fileInput" multiple accept="image/*,video/*" class="hidden" @change="handleFilesSelected" />
          </div>

          <!-- Newly Selected Media Files Previews -->
          <div v-if="newFiles.length > 0" class="flex flex-wrap gap-2 pt-1">
            <div v-for="(file, idx) in newFiles" :key="idx" class="relative bg-zinc-800 border border-zinc-700 px-3 py-1.5 rounded-lg flex items-center gap-2 text-xs">
              <span class="text-zinc-300 truncate max-w-[150px]">{{ file.name }}</span>
              <button @click="removeNewFile(idx)" type="button" class="text-zinc-500 hover:text-red-400">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
          </div>
        </div>

      </div>

      <!-- Modal Footer -->
      <div class="px-6 py-4 border-t border-zinc-800 flex justify-end gap-3 shrink-0">
        <button @click="$emit('close')" type="button" class="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors">
          Annuler
        </button>
        <button v-if="step === 'form'" @click="savePoi" type="button"
                class="bg-sky-500 hover:bg-sky-400 text-zinc-950 px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors flex items-center gap-2"
                :disabled="submitting || !form.name.trim()">
          <span v-if="submitting" class="w-4 h-4 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin"></span>
          {{ submitting ? 'Enregistrement...' : 'Enregistrer' }}
        </button>
      </div>

    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, reactive, nextTick } from 'vue'

const props = withDefaults(defineProps<{
  show: boolean
  poi: any | null
  existingTags?: string[]
}>(), {
  existingTags: () => []
})

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved'): void
}>()

// --- Step management ---
// 'search' = step 1 (autocomplete only), 'form' = step 2 (full form)
const step = ref<'search' | 'form'>('search')

const form = reactive({
  name: '',
  description: '',
  latitude: null as number | null,
  longitude: null as number | null,
  address: '',
  country: '',
  region: '',
  city: '',
  tags: [] as string[],
  links: [] as Array<{ title: string, url: string }>,
  media: [] as any[]
})

// Address UI state
const showDetailedAddress = ref(false)
const showAddressSearch = ref(false)

// Search state
const searchAddressQuery = ref('')
const suggestions = ref<any[]>([])
const suggestionsLoading = ref(false)
const searchInputRef = ref<HTMLInputElement | null>(null)

// AI state
const aiSuggesting = ref(false)
const suggestedImageUrl = ref('')
const keepSuggestedImage = ref(true)

// File state
const newTag = ref('')
const newFiles = ref<File[]>([])
const fileInput = ref<HTMLInputElement | null>(null)
const submitting = ref(false)

// Reset when modal opens
watch(() => props.show, (newVal) => {
  if (newVal) {
    newFiles.value = []
    searchAddressQuery.value = ''
    suggestions.value = []
    suggestedImageUrl.value = ''
    keepSuggestedImage.value = true
    showDetailedAddress.value = false
    showAddressSearch.value = false

    if (props.poi) {
      // Edit mode: go straight to form
      step.value = 'form'
      form.name = props.poi.name || ''
      form.description = props.poi.description || ''
      form.latitude = props.poi.latitude || null
      form.longitude = props.poi.longitude || null
      form.address = props.poi.address || ''
      form.country = props.poi.country || ''
      form.region = props.poi.region || ''
      form.city = props.poi.city || ''
      form.tags = [...(props.poi.tags || [])]
      form.links = JSON.parse(JSON.stringify(props.poi.links || []))
      form.media = [...(props.poi.media || [])]
    } else {
      // Create mode: start at search step
      step.value = 'search'
      form.name = ''
      form.description = ''
      form.latitude = null
      form.longitude = null
      form.address = ''
      form.country = ''
      form.region = ''
      form.city = ''
      form.tags = []
      form.links = []
      form.media = []
    }

    // Focus search input on next tick
    if (!props.poi) {
      nextTick(() => searchInputRef.value?.focus())
    }
  }
})

// Skip autocomplete, go directly to form
const skipToForm = () => {
  step.value = 'form'
}

// Tags management
const addTag = () => {
  const clean = newTag.value.trim()
  if (clean && !form.tags.includes(clean)) {
    form.tags.push(clean)
  }
  newTag.value = ''
}
const removeTag = (tag: string) => {
  form.tags = form.tags.filter(t => t !== tag)
}

// Links management
const addLink = () => {
  form.links.push({ title: '', url: '' })
}
const removeLink = (idx: number) => {
  form.links.splice(idx, 1)
}

// Files management
const triggerFileInput = () => {
  fileInput.value?.click()
}
const handleFilesSelected = (e: Event) => {
  const target = e.target as HTMLInputElement
  if (target.files) {
    for (let i = 0; i < target.files.length; i++) {
      newFiles.value.push(target.files[i])
    }
  }
}
const handleFilesDrop = (e: DragEvent) => {
  if (e.dataTransfer?.files) {
    for (let i = 0; i < e.dataTransfer.files.length; i++) {
      newFiles.value.push(e.dataTransfer.files[i])
    }
  }
}
const removeNewFile = (idx: number) => {
  newFiles.value.splice(idx, 1)
}

// Delete media already uploaded
const deleteExistingMedia = async (mediaId: number) => {
  if (!confirm('Voulez-vous vraiment supprimer cette photo/vidéo ?')) return
  try {
    const token = localStorage.getItem('apiToken')
    const response = await fetch(`/api/explorer/media/${mediaId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    })
    if (response.ok) {
      form.media = form.media.filter(m => m.id !== mediaId)
    }
  } catch (e) {
    console.error(e)
  }
}

// Save POI
const savePoi = async () => {
  if (!form.name.trim()) return

  submitting.value = true
  try {
    const token = localStorage.getItem('apiToken')

    const url = props.poi ? `/api/explorer/pois/${props.poi.id}` : '/api/explorer/pois'
    const method = props.poi ? 'PUT' : 'POST'

    const response = await fetch(url, {
      method,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: form.name,
        description: form.description,
        latitude: form.latitude,
        longitude: form.longitude,
        address: form.address,
        country: form.country,
        region: form.region,
        city: form.city,
        tags: form.tags,
        links: form.links.filter(l => l.title.trim() && l.url.trim()),
        suggestedImageUrl: suggestedImageUrl.value && keepSuggestedImage.value ? suggestedImageUrl.value : null
      })
    })

    if (!response.ok) {
      throw new Error('Erreur lors de la sauvegarde.')
    }

    const savedPoi = await response.json()
    const poiId = savedPoi.id

    // Upload newly added files
    if (newFiles.value.length > 0) {
      for (const file of newFiles.value) {
        const formData = new FormData()
        formData.append('media', file)
        await fetch(`/api/explorer/pois/${poiId}/media`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        })
      }
    }

    emit('saved')
    emit('close')
  } catch (e) {
    console.error(e)
    alert('Une erreur est survenue lors de l\'enregistrement.')
  } finally {
    submitting.value = false
  }
}

// Nominatim autocompletion (shared)
let debounceTimer: any = null
const handleAddressSearchInput = () => {
  if (debounceTimer) clearTimeout(debounceTimer)
  const q = searchAddressQuery.value.trim()
  if (q.length < 3) {
    suggestions.value = []
    return
  }

  suggestionsLoading.value = true
  debounceTimer = setTimeout(async () => {
    try {
      const token = localStorage.getItem('apiToken')
      const response = await fetch(`/api/explorer/geocoding/search?q=${encodeURIComponent(q)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        suggestions.value = await response.json()
      }
    } catch (e) {
      console.error(e)
    } finally {
      suggestionsLoading.value = false
    }
  }, 400)
}

// Fill form from a suggestion (step 1 — triggers AI suggest)
const selectSuggestion = async (sug: any) => {
  fillFormFromSuggestion(sug)
  suggestions.value = []
  searchAddressQuery.value = ''

  // Move to form while AI is working
  step.value = 'form'

  // Call AI suggest endpoint
  aiSuggesting.value = true
  suggestedImageUrl.value = ''
  try {
    const token = localStorage.getItem('apiToken')
    const response = await fetch('/api/explorer/ai/suggest', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        address: form.address,
        latitude: form.latitude,
        longitude: form.longitude,
        existingTags: props.existingTags
      })
    })

    if (response.ok) {
      const data = await response.json()
      if (data.name) form.name = data.name
      if (data.description) form.description = data.description
      if (data.tags && data.tags.length > 0) {
        data.tags.forEach((tag: string) => {
          if (!form.tags.includes(tag)) form.tags.push(tag)
        })
      }
      if (data.imageUrl) {
        suggestedImageUrl.value = data.imageUrl
        keepSuggestedImage.value = true
      }
    }
  } catch (e) {
    console.error(e)
  } finally {
    aiSuggesting.value = false
  }
}

// Fill form from a suggestion inside the form (step 2 — no AI call)
const selectSuggestionInForm = (sug: any) => {
  fillFormFromSuggestion(sug)
  suggestions.value = []
  searchAddressQuery.value = ''
  showAddressSearch.value = false
  // Show detailed address so the user sees what was filled
  showDetailedAddress.value = true
}

// Shared: extract address fields from a Nominatim suggestion
const fillFormFromSuggestion = (sug: any) => {
  form.address = sug.display_name
  form.latitude = parseFloat(sug.lat)
  form.longitude = parseFloat(sug.lon)
  const addr = sug.address || {}
  form.country = addr.country || ''
  form.region = addr.state || addr.region || addr.county || ''
  form.city = addr.city || addr.town || addr.village || addr.municipality || addr.suburb || ''
}
</script>
